/**
 * Custom "Shop the look" drawer
 * -----------------------------
 * Drives the <shop-the-look> element (see snippets/custom-shop-the-look.liquid):
 *   - Opens a side drawer (from the configured side; full width on mobile). Open/close
 *     is class-only (`is-open`); visibility + slide are pure CSS, so there are no
 *     timers/transitionend handlers that could mis-fire and auto-close the drawer.
 *   - Moves the drawer to #Portal (body level) so position:fixed isn't clipped by a
 *     transformed/sticky ancestor in the product info column.
 *   - Size pills set the hidden variant input. The add-to-cart button is always
 *     enabled; submitting with no size chosen is blocked (and shakes the picker).
 *   - Add-to-cart itself is handled by the theme's own <product-form> element (AJAX
 *     cart); if that isn't present the form still POSTs to /cart/add normally.
 *
 * Non-invasive: only manages its own elements and the body scroll-lock class, which
 * is always released on close and on disconnect (so the page can never stay locked).
 */
(function () {
  'use strict';

  if (window.customElements && customElements.get('shop-the-look')) return;

  class ShopTheLook extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-stl-open]');
      // The drawer is portaled out of this element on first connect, so cache it
      // and only query while it still lives inside.
      if (!this.drawer) this.drawer = this.querySelector('[data-stl-drawer]');
      if (!this.trigger || !this.drawer) return;

      // Portal the drawer to body level (re-attach the cached node on reconnect).
      var portal = document.getElementById('Portal') || document.body;
      if (this.drawer.parentElement !== portal) portal.appendChild(this.drawer);
      this.drawer.removeAttribute('hidden');

      if (this._bound) return;
      this._bound = true;
      this._onKeydown = this._onKeydown.bind(this);

      this.trigger.addEventListener('click', () => this.open());

      this.drawer.querySelectorAll('[data-stl-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });

      // Size pill selection (delegated within the drawer).
      this.drawer.addEventListener('click', (event) => {
        var pill = event.target.closest('[data-stl-size]');
        if (pill && !pill.disabled) this._selectSize(pill);
      });

      // Guard: never let a form submit without a chosen variant (capture phase
      // runs before the theme's <product-form> handler, so it can block it).
      this.drawer.addEventListener(
        'submit',
        (event) => {
          var form = event.target;
          var input = form.querySelector('[data-product-form-variant]');
          if (input && !input.value) {
            event.preventDefault();
            event.stopImmediatePropagation();
            var item = form.closest('[data-stl-item]');
            var sizes = item && item.querySelector('[data-stl-sizes]');
            if (sizes) {
              sizes.classList.add('stl-shake');
              setTimeout(() => sizes.classList.remove('stl-shake'), 400);
            }
          }
        },
        true
      );
    }

    disconnectedCallback() {
      // Never leave the page scroll-locked if the element is removed/re-rendered.
      document.body.classList.remove('stl-lock');
      if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
      // Detach the portaled drawer; the cached reference lets a reconnect re-attach it.
      if (this.drawer) this.drawer.remove();
    }

    open() {
      this.drawer.classList.add('is-open');
      document.body.classList.add('stl-lock');
      document.addEventListener('keydown', this._onKeydown);
      var closeBtn = this.drawer.querySelector('.stl__close');
      if (closeBtn) closeBtn.focus();
    }

    close() {
      this.drawer.classList.remove('is-open');
      document.body.classList.remove('stl-lock');
      document.removeEventListener('keydown', this._onKeydown);
      if (this.trigger) this.trigger.focus();
    }

    _onKeydown(event) {
      if (event.key === 'Escape') this.close();
    }

    _selectSize(pill) {
      var item = pill.closest('[data-stl-item]');
      if (!item) return;

      item.querySelectorAll('[data-stl-size]').forEach((p) => {
        p.classList.toggle('is-selected', p === pill);
      });

      var input = item.querySelector('[data-product-form-variant]');
      if (input) input.value = pill.getAttribute('data-variant-id') || '';
      // The add-to-cart button stays enabled at all times; submitting without a
      // chosen size is blocked (and shakes the size picker) by the submit guard.
    }
  }

  customElements.define('shop-the-look', ShopTheLook);
})();
